/**
 * BST Troubleshooting — category nav, model tabs, accordions
 */
(function () {
  'use strict';

  const ACTIVE_CLASS = 'is-active';
  const OPEN_CLASS = 'is-open';

  function setAccordionOpen(item, open) {
    const trigger = item.querySelector('[data-bst-faq-trigger]');
    const answer = item.querySelector('[data-bst-faq-answer]');

    item.classList.toggle(OPEN_CLASS, open);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (answer) {
      if (open) {
        answer.removeAttribute('inert');
      } else {
        answer.setAttribute('inert', '');
      }
    }
  }

  function bindAccordions(scope, singleOpen) {
    scope.querySelectorAll('[data-bst-faq-accordion]').forEach((group) => {
      if (group._bstTroubleshootingAccordionInit) return;
      group._bstTroubleshootingAccordionInit = true;

      group.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-bst-faq-trigger]');
        if (!trigger || !group.contains(trigger)) return;

        const item = trigger.closest('[data-bst-faq-item]');
        if (!item || !group.contains(item)) return;

        const isOpen = item.classList.contains(OPEN_CLASS);

        if (singleOpen && !isOpen) {
          group.querySelectorAll('[data-bst-faq-item].is-open').forEach((other) => {
            if (other !== item) setAccordionOpen(other, false);
          });
        }

        setAccordionOpen(item, !isOpen);
      });
    });
  }

  function activateModelTab(category, tabKey) {
    category.querySelectorAll('[data-bst-troubleshooting-model-tab]').forEach((tab) => {
      const isActive = tab.dataset.bstTroubleshootingModelTab === tabKey;
      tab.classList.toggle(ACTIVE_CLASS, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    category.querySelectorAll('[data-bst-troubleshooting-model-panel]').forEach((panel) => {
      const isActive = panel.dataset.bstTroubleshootingModelPanel === tabKey;
      panel.classList.toggle(ACTIVE_CLASS, isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  }

  function bindModelTabs(category) {
    const tabs = category.querySelectorAll('[data-bst-troubleshooting-model-tab]');
    const panels = category.querySelectorAll('[data-bst-troubleshooting-model-panel]');
    if (!tabs.length || !panels.length) return;

    const panelKeys = new Set(
      Array.from(panels).map((panel) => panel.dataset.bstTroubleshootingModelPanel)
    );
    const validTabs = Array.from(tabs).filter((tab) =>
      panelKeys.has(tab.dataset.bstTroubleshootingModelTab)
    );
    if (!validTabs.length) return;

    const initial =
      category.querySelector('[data-bst-troubleshooting-model-tab].is-active')?.dataset
        .bstTroubleshootingModelTab || validTabs[0].dataset.bstTroubleshootingModelTab;

    activateModelTab(category, initial);

    validTabs.forEach((tab) => {
      tab.addEventListener('click', () => activateModelTab(category, tab.dataset.bstTroubleshootingModelTab));

      tab.addEventListener('keydown', (event) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();
        const index = validTabs.indexOf(tab);
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % validTabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + validTabs.length) % validTabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = validTabs.length - 1;

        validTabs[nextIndex]?.focus();
        activateModelTab(category, validTabs[nextIndex].dataset.bstTroubleshootingModelTab);
      });
    });
  }

  function bindCategory(category) {
    if (category._bstTroubleshootingCategoryInit) return;
    category._bstTroubleshootingCategoryInit = true;

    const singleOpen = category.dataset.singleOpen === 'true';
    bindAccordions(category, singleOpen);
    bindModelTabs(category);
  }

  function getCategories() {
    return Array.from(document.querySelectorAll('[data-bst-troubleshooting-category-id]'));
  }

  function activateCategory(categoryId) {
    getCategories().forEach((category) => {
      const isActive = category.dataset.bstTroubleshootingCategoryId === categoryId;
      category.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        category.removeAttribute('inert');
      } else {
        category.setAttribute('inert', '');
      }
    });
  }

  function setActiveNavTab(nav, categoryId) {
    const tabs = nav.querySelectorAll('[data-bst-troubleshooting-nav-link]');
    tabs.forEach((tab) => {
      const isActive = tab.dataset.bstTroubleshootingNavLink === categoryId;
      tab.classList.toggle(ACTIVE_CLASS, isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  function bindNav(nav) {
    if (nav._bstTroubleshootingNavInit) return;
    nav._bstTroubleshootingNavInit = true;

    const tabs = Array.from(nav.querySelectorAll('[data-bst-troubleshooting-nav-link]'));
    const categories = getCategories();
    if (!tabs.length || !categories.length) return;

    const categoryIds = new Set(
      categories.map((category) => category.dataset.bstTroubleshootingCategoryId)
    );
    const validTabs = tabs.filter((tab) => categoryIds.has(tab.dataset.bstTroubleshootingNavLink));
    if (!validTabs.length) return;

    const initial =
      categories.find((category) => category.classList.contains(ACTIVE_CLASS))?.dataset
        .bstTroubleshootingCategoryId ||
      validTabs.find((tab) => tab.classList.contains(ACTIVE_CLASS))?.dataset.bstTroubleshootingNavLink ||
      validTabs[0].dataset.bstTroubleshootingNavLink;

    activateCategory(initial);
    setActiveNavTab(nav, initial);

    validTabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const categoryId = tab.dataset.bstTroubleshootingNavLink;
        activateCategory(categoryId);
        setActiveNavTab(nav, categoryId);
      });

      tab.addEventListener('keydown', (event) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();
        const index = validTabs.indexOf(tab);
        let nextIndex = index;

        if (event.key === 'ArrowRight') nextIndex = (index + 1) % validTabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + validTabs.length) % validTabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = validTabs.length - 1;

        const nextTab = validTabs[nextIndex];
        if (!nextTab) return;

        nextTab.focus();
        activateCategory(nextTab.dataset.bstTroubleshootingNavLink);
        setActiveNavTab(nav, nextTab.dataset.bstTroubleshootingNavLink);
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-bst-troubleshooting-category]').forEach(bindCategory);
    document.querySelectorAll('[data-bst-troubleshooting-nav]').forEach(bindNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
